import { AbstractMesh, DeepImmutable, InstancedMesh, Observer, Quaternion, Scene, StaticSound, Vector3 } from '@babylonjs/core';
import { Level } from '../Level';
import { EntitySoundConfig } from '../LevelConfig';
import { Player } from '../Player';
import { MeshBehavior } from './MeshBehavior';
export abstract class AbstractMeshBehavior implements MeshBehavior {
  level: Level;
  mesh: AbstractMesh;
  sound: StaticSound;

  observer: Observer<Scene>;

  get attachedNode(): AbstractMesh {
    return this.mesh;
  }

  constructor(public name: string) {}

  abstract clone(): MeshBehavior;

  with(property: string, value: any): AbstractMeshBehavior {
    this[property] = value;
    return this;
  }

  playerCollision(player: Player): void {}

  beforeRender(): void {}

  init(): void {}

  initSound() {}

  stopSound() {
    if (this.sound) {
      this.sound.stop();
    }
  }

  playSound() {
    if (this.sound) {
      this.sound.play();
    }
  }

  setSound(config: EntitySoundConfig) {
    if (config) {
      const onNewSound = (sound: StaticSound) => {
        if (!sound) {
          return;
        }
        this.sound = sound;
        if (config.autoplay) {
          this.sound.loop = true;
          this.sound.play();
        }
      };

      if (config.fileName) {
        this.level.game.sound.create(config.name, config.fileName, 'fx').then((sound) => {
          this.level.game.sound.spatial(config.name, this.mesh).then(onNewSound);
        });
      } else {
        this.level.game.sound.spatial(config.name, this.mesh).then(onNewSound);
      }
    }
  }

  attach(mesh: AbstractMesh): void {
    if (!mesh) {
      throw new Error('Mesh is null');
    }
    this.mesh = mesh;
    if (this.level && mesh instanceof InstancedMesh) {
      this.attachObserver();
    }
  }

  detach(): void {
    this.detachObserver();
    this.mesh = null;
    if (this.sound) {
      this.sound.dispose();
      this.sound = null;
    }
  }

  initLevel(level: Level): AbstractMeshBehavior {
    this.level = level;
    this.initSound();
    if (this.mesh && this.mesh instanceof InstancedMesh && !this.observer) {
      this.attachObserver();
    }
    return this;
  }

  private attachObserver() {
    if (this.observer) return;
    this.observer = this.level.scene.onBeforeRenderObservable.add(() => {
      this.beforeRender();
    });
  }

  private detachObserver() {
    if (this.observer) {
      this.level.scene.onBeforeRenderObservable.remove(this.observer);
      this.observer = null;
    }
  }

  setMetaData(key: string, value: any): void {
    this.setMeshMetaData(this.mesh, key, value);
  }

  setMeshMetaData(mesh: AbstractMesh, key: string, value: any): void {
    if (!mesh.metadata) {
      mesh.metadata = {};
    }
    mesh.metadata[key] = value;
  }

  rotateAroundPivot(pivotPoint: Vector3, axis: DeepImmutable<Vector3>, angle?: number) {
    let rq: Quaternion = this.mesh.rotationQuaternion;
    if (!rq) {
      rq = Quaternion.RotationYawPitchRoll(this.mesh.rotation.y, this.mesh.rotation.x, this.mesh.rotation.z);
    }
    const p = new Quaternion(
      this.mesh.position.x - pivotPoint.x,
      this.mesh.position.y - pivotPoint.y,
      this.mesh.position.z - pivotPoint.z,
      0
    );
    const normalizedAxis = axis.normalizeToNew();
    const q = Quaternion.RotationAxis(normalizedAxis, angle); //form quaternion rotation
    const qinv = Quaternion.Inverse(q);
    const pdash = q.multiply(p).multiply(qinv);
    this.mesh.position = new Vector3(pivotPoint.x + pdash.x, pivotPoint.y + pdash.y, pivotPoint.z + pdash.z);
    this.mesh.rotationQuaternion = q.multiply(rq);
  }

  scaleFromPivot(pivotPoint: Vector3, sx: number, sy: number, sz: number) {
    const scaleX = sx / this.mesh.scaling.x;
    const scaleY = sy / this.mesh.scaling.y;
    const scaleZ = sz / this.mesh.scaling.z;
    this.mesh.scaling = new Vector3(sx, sy, sz);
    this.mesh.position = new Vector3(
      pivotPoint.x + scaleX * (this.mesh.position.x - pivotPoint.x),
      pivotPoint.y + scaleY * (this.mesh.position.y - pivotPoint.y),
      pivotPoint.z + scaleZ * (this.mesh.position.z - pivotPoint.z)
    );
  }
}
